// ShareBite Donation collection & completion decision (Task 4.3).
//
// Pure, platform-independent authorization decision for an NGO marking a
// Donation as Collected. Collection auto-completes: a successful collection
// walks the lifecycle Reserved -> Collected -> Completed with no Restaurant
// confirmation required (Req 14.3, 14.4), so the resulting stored status is
// Completed.
//
// The caller (a Cloud Function running inside a Firestore transaction) is
// responsible for persisting the result; this module performs no I/O and has
// no side effects, so the same rule runs on client and server.
//
// This module is intentionally self-contained: it inlines the
// Reserved -> Collected -> Completed lifecycle rather than depending on the
// status-transition module.
//
// Design reference: design.md "Property 8: Collection authorization and
// completion".
// Requirements:
//   14.1 - While Reserved, the reserving NGO may mark the Donation Collected.
//   14.2 - Marking Collected sets the status to Collected.
//   14.3 - A Collected Donation is automatically set to Completed.
//   14.5 - An NGO other than the reserving NGO is rejected; status preserved.
//   14.6 - A request while not Reserved is rejected; status preserved.
/** Error code: the requester is not the reserving NGO (Req 14.5). */
export const NOT_AUTHORIZED_TO_COLLECT = "requesting NGO is not authorized to collect the donation";
/** Error code: the Donation is not in a collectable state (Req 14.6). */
export const NOT_COLLECTABLE_STATE = "donation is not in a collectable state";
/**
 * Apply the collection-and-completion decision for a requesting NGO.
 *
 * Succeeds if and only if BOTH conditions hold:
 *  - the Donation's status is exactly `"Reserved"` (Req 14.1), AND
 *  - `requesterUid` equals the Donation's `reservedBy` (Req 14.5).
 *
 * On success, collection auto-completes (Reserved -> Collected -> Completed,
 * Req 14.2/14.3) and the result is `{ ok: true, status: "Completed" }`.
 *
 * On any unauthorized or invalid request the stored status is preserved
 * unchanged (Req 14.5, 14.6). When the status is not Reserved the request is
 * rejected as not collectable (Req 14.6); when the status is Reserved but the
 * requester is not the reserving NGO the request is rejected as unauthorized
 * (Req 14.5). The not-collectable check is evaluated first so that a request
 * against a non-Reserved Donation is never reported as merely unauthorized.
 *
 * This function is pure: it reads only its arguments, mutates nothing, and
 * produces no side effects.
 *
 * @param state        The Donation's current collection-relevant state.
 * @param requesterUid The uid of the NGO requesting collection.
 * @returns A {@link CollectionResult} describing the outcome.
 */
export function markCollected(state, requesterUid) {
    if (state.status !== "Reserved") {
        return {
            ok: false,
            status: state.status,
            error: NOT_COLLECTABLE_STATE,
        };
    }
    if (state.reservedBy === null || requesterUid !== state.reservedBy) {
        return {
            ok: false,
            status: state.status,
            error: NOT_AUTHORIZED_TO_COLLECT,
        };
    }
    return { ok: true, status: "Completed" };
}
//# sourceMappingURL=collection.js.map